import { useEffect, useState } from "react";

import { activeBranch } from "./tokens";

/**
 * The active branch, as a value that re-renders when it arrives.
 *
 * **Null is a real state and the common one on a first sign-in**: nothing is in
 * storage, and the branch switcher only writes a branch once it has loaded the
 * caller's memberships. A screen that read `activeBranch.get()` once on mount
 * saw null, sent a branch-scoped request with no header, was refused, and had
 * nothing to retry on — which surfaced as an empty list rather than an error.
 *
 * Anything branch-scoped should use this and treat null as "not yet", not as
 * "none".
 */
export function useActiveBranch(): string | null {
  const [branchId, setBranchId] = useState<string | null>(() => activeBranch.get());

  useEffect(() => {
    /**
     * **Re-read before subscribing, because a parent misses a child's write.**
     *
     * `useState`'s initialiser runs in the render phase; this effect runs after
     * it. Anything that wrote the branch in between notified a listener set
     * this component was not yet in, and the value it is holding is stale with
     * nothing left to correct it — `localStorage` fires no event in the tab
     * that wrote it, which is why `subscribe` exists at all.
     *
     * That gap is not hypothetical: **React runs child effects before parent
     * effects**, and `BranchPage` sets the active branch from the URL in its
     * own mount effect. So `SettingsLayout`, which renders it through an
     * `<Outlet/>` and subscribes for the rail's switcher, reliably missed it —
     * the rail named the location you came from while the page showed the one
     * you had opened. Caught in the browser at 1440px, not by a test.
     *
     * One extra read of `localStorage` per mount, and the race is closed for
     * every caller rather than worked around in the one that noticed.
     */
    setBranchId(activeBranch.get());

    return activeBranch.subscribe(setBranchId);
  }, []);

  return branchId;
}
